import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { INSIGHTS, todayTip, todayLesson } from "@/lib/insights";
import { buildCollectionLd } from "@/lib/page-schemas";

export const Route = createFileRoute("/learn")({
  head: () => ({
    meta: [
      { title: "Gold Buying Tips & 60-Second Lessons — DrHint" },
      {
        name: "description",
        content:
          "Practical, plain-language gold tips and lessons — hallmarks, making charges, 22K vs 24K, wastage, buybacks and more. A new tip is featured every day.",
      },
      { property: "og:title", content: "Gold Buying Tips & Lessons" },
      { property: "og:description", content: "Daily, original tips that protect you from overpaying at the jeweller." },
      { property: "og:url", content: "https://drhint.com/learn" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/learn" }],
    scripts: buildCollectionLd({
      url: "https://drhint.com/learn",
      name: "DrHint Gold Tips & 60-Second Lessons",
      description: "Short, original tips and bite-sized lessons that protect gold buyers from overpaying at the jeweller.",
      items: INSIGHTS.map((i) => ({ url: `https://drhint.com/learn/${i.slug}`, name: i.title, description: i.body.slice(0, 160) })),
      trail: [
        { name: "Home", item: "https://drhint.com/" },
        { name: "Learn", item: "https://drhint.com/learn" },
      ],
    }),
  }),
  component: LearnIndex,
});

function LearnIndex() {
  const tip = todayTip();
  const lesson = todayLesson();
  const tips = INSIGHTS.filter((i) => i.type === "tip");
  const lessons = INSIGHTS.filter((i) => i.type === "lesson");

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl md:text-4xl font-bold">Learn before you buy gold</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Short, original tips and 60-second lessons — written so you walk into a jeweller informed.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <Link
            to="/learn/$slug"
            params={{ slug: tip.slug }}
            className="block rounded-2xl border border-amber-400/40 bg-amber-500/5 p-6 hover:bg-amber-500/10"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-600">Today's tip</div>
            <div className="mt-2 text-xl font-bold">{tip.title}</div>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{tip.body}</p>
          </Link>
          <Link
            to="/learn/$slug"
            params={{ slug: lesson.slug }}
            className="block rounded-2xl border border-border bg-card p-6 hover:bg-accent/40"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's lesson</div>
            <div className="mt-2 text-xl font-bold">{lesson.title}</div>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{lesson.body}</p>
          </Link>
        </div>

        <h2 className="mt-12 text-2xl font-bold">All quick tips</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {tips.map((t) => (
            <li key={t.slug}>
              <Link to="/learn/$slug" params={{ slug: t.slug }} className="block rounded-lg border border-border bg-card px-4 py-3 hover:bg-accent/40">
                <div className="font-semibold text-sm">{t.title}</div>
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-12 text-2xl font-bold">60-second lessons</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {lessons.map((t) => (
            <li key={t.slug}>
              <Link to="/learn/$slug" params={{ slug: t.slug }} className="block rounded-lg border border-border bg-card px-4 py-3 hover:bg-accent/40">
                <div className="font-semibold text-sm">{t.title}</div>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </Layout>
  );
}